/**
 * Magic numbers for synthetic security/series generation (plan §0.2 · §3.3).
 * Ported from demos/Meridian Terminal.dc.html.
 */
import type { WINDOW_KEYS } from '@meridian/contracts';

/** Price = round((PRICE_BASE + R() * PRICE_SPAN) * 100) / 100 */
export const PRICE_BASE = 26;
export const PRICE_SPAN = 520;

/** Vol multiplier vk ∈ [VK_LO, VK_LO + VK_SPAN]. */
export const VK_LO = 0.6;
export const VK_SPAN = 0.95;

/** Beta = round((BETA_BASE + |f0| * (BETA_F0_LO + R() * BETA_F0_SPAN)) * 100) / 100 */
export const BETA_BASE = 0.3;
export const BETA_F0_LO = 0.55;
export const BETA_F0_SPAN = 0.85;

/**
 * Per-window return spreads scaled by vk, in WINDOW_KEYS order.
 *
 * The `satisfies` clause length-locks this tuple to WINDOW_KEYS: adding a fifth
 * window key fails to compile here rather than yielding `undefined` at runtime,
 * which `noUncheckedIndexedAccess` cannot catch through the index cast in
 * universe/derive-security.ts.
 */
export const RETURN_SPREADS = Object.freeze([0.085, 0.16, 0.42, 0.95] as const) satisfies {
  readonly length: (typeof WINDOW_KEYS)['length'];
};

/** Idiosyncratic noise on return: (R()*2-1) * spread * RETURN_NOISE. */
export const RETURN_NOISE = 0.4;

/** Drawdown gen: -(vol * sqrt(WYR) * (DD_GEN_LO + R() * DD_GEN_SPAN) + DD_GEN_ADD) */
export const DD_GEN_LO = 0.7;
export const DD_GEN_SPAN = 0.85;
export const DD_GEN_ADD = 0.012;

/** Drawdown floor and epsilon vs period return (plan §0.2). */
export const DD_FLOOR = -0.92;
export const DD_EPSILON = 0.015;

/** Factor loading jitter ± FACTOR_JITTER. */
export const FACTOR_JITTER = 0.3;

/** Idio residual for factor norm: IDIO_LO + R() * IDIO_SPAN. */
export const IDIO_LO = 0.5;
export const IDIO_SPAN = 0.55;

/** Series return floor: max(SERIES_RET_FLOOR, 1 + ret[w]). */
export const SERIES_RET_FLOOR = 0.15;

/**
 * Fixed sigma for the index-sparkline walk (demo line 890). Unused until that
 * walk is ported — lifted here so the literal does not get re-typed inline, and
 * so the unported fifth RNG stream documented in rand/seed.ts has a home.
 * Note the demo's sparkline walk applies NO SERIES_RET_FLOOR and no price
 * rescale; it is not a variant of generateSeries.
 */
export const INDEX_SPARKLINE_SIGMA = 0.16;
